import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { AZURE } from '../services/api';
import { normalizeRole } from '../navigation/routeRegistry';

/**
 * RBACContext — Role-Based Access Control
 * Carrega roles, permissões e vínculos do usuário via GET /api/me
 * Fornece helpers: hasRole, hasPermission, hasAnyRole, activeRole
 */

const RBACContext = createContext();

export const useRBAC = () => {
  const context = useContext(RBACContext);
  if (!context) {
    throw new Error('useRBAC deve ser usado dentro de um RBACProvider');
  }
  return context;
};

// Roles disponíveis
export const ROLES = {
  GESTOR: 'GESTOR',
  TREINADOR: 'TREINADOR',
  PREPARADOR_FISICO: 'PREPARADOR_FISICO',
  ATLETA: 'ATLETA',
  NUTRICIONISTA: 'NUTRICIONISTA',
  BIOMECANICO: 'BIOMECANICO',
  // RECRUITER: 'RECRUITER', // Fase 2
};

// Roles that have category-scoped access to athletes
export const STAFF_ROLES = ['TREINADOR', 'PREPARADOR_FISICO', 'NUTRICIONISTA', 'BIOMECANICO'];

// Labels PT-BR
export const ROLE_LABELS = {
  GESTOR: 'Gestor',
  TREINADOR: 'Treinador',
  PREPARADOR_FISICO: 'Preparador Físico',
  ATLETA: 'Atleta',
  NUTRICIONISTA: 'Nutricionista',
  BIOMECANICO: 'Biomecânico',
  // RECRUITER: 'Recruiter', // Fase 2
};

// Ícones por role (lucide-react keys)
export const ROLE_ICONS = {
  GESTOR: 'settings',
  TREINADOR: 'clipboard',
  PREPARADOR_FISICO: 'dumbbell',
  ATLETA: 'activity',
  NUTRICIONISTA: 'apple',
  BIOMECANICO: 'scan-line',
  // RECRUITER: 'search', // Fase 2
};

export const RBACProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  const [rbacData, setRbacData] = useState(null);
  const [activeRole, setActiveRole] = useState(null);
  // ⚠️ CRITICAL: Começa como true para evitar race condition
  // Se começar false, RequireRole vê loading=false + roles=[] no 1o render
  // e redireciona imediatamente → /feed ↔ /athlete redirect loop
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carrega contexto RBAC do backend
  const loadRBACContext = useCallback(async () => {
    if (!isAuthenticated || !user?.token) {
      // Não autenticado — parar loading e sair
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data } = await AZURE.get('/me');
      if (data.success) {
        // Normalize all role_keys via ROLE_MAP
        const rawRoles = data.data.roles || [];
        let normalized = rawRoles.map(r => ({
          ...r,
          role_key: normalizeRole(r.role_key) || r.role_key,
        }));

        // ── Internal user check: MUST come before any fallback logic ──
        const isInternalUser = data.data.user?.is_internal === 1 || data.data.user?.is_internal === true;
        if (isInternalUser) {
          normalized = [{ role_key: 'OPERATIONS_ADMIN', is_primary: true }];
        }

        // ── Dev logging: raw vs normalized (P0 diagnostics) ──
        if (process.env.NODE_ENV === 'development') {
          const rawKeys = rawRoles.map(r => r.role_key);
          const normKeys = normalized.map(r => r.role_key);
          console.log('[RBAC] /api/me response:', {
            rawRoles: rawKeys,
            normalizedRoles: normKeys,
            primary_role: data.data.primary_role,
            roleCount: normKeys.length,
          });
          // Warn on normalization misses
          const missed = rawKeys.filter((r, i) => r && !normKeys[i]);
          if (missed.length > 0) {
            console.warn('[RBAC] ⚠️ Roles lost during normalization (not in ROLE_MAP):', missed);
          }
        }

        // ── Fallback A (jul/2026, ESTREITADO — patologia A): sintese de role
        // SOMENTE na janela register->/start que o backend documenta
        // (routes_auth.py:205 "unlocks GESTOR fallback in RBACContext"):
        // register intent=club cria SO o user (user_type=club_admin,
        // onboarding_status=pending) — institution e role GESTOR reais so
        // nascem no /onboarding/start. A janela exige CONJUNCAO:
        //   user_type === 'club_admin'  (quem: populacao do register club)
        //   onboarding_status === 'pending'  (quando: SERVER-truth do /me —
        //       nunca localStorage; pos-discard e 'none', completed fecha)
        // FORA da janela: roles=[] fail-closed — RouteGuard branch 0 nega
        // role-gated, public-auth (feed/chat) segue livre. Removidos:
        //   (1) mapa user_type->role generico + default ATLETA universal —
        //       sintetizava role pra QUALQUER user sem role no DB (matriz
        //       09/07: zero staff/atletas legitimos dependiam; os 22 sem
        //       role real eram todos club_admin teste/fantasma);
        //   (2) super-admin por email (ALL_ROLES) — email/role do
        //       localStorage sao editaveis pelo cliente, mesmo vetor da
        //       patologia C (8dad8af). Super-admin real tem roles reais.
        if (normalized.length === 0 && !isInternalUser) {
          const userType = data.data.user?.user_type || user?.user_type;
          const onbStatus = data.data.user?.onboarding_status; // SERVER-truth
          const inSetupWindow = userType === 'club_admin' && onbStatus === 'pending';

          if (inSetupWindow) {
            normalized = [{ role_key: 'GESTOR' }];
          }

          if (process.env.NODE_ENV === 'development') {
            console.warn(
              inSetupWindow
                ? '[RBAC] Zero roles do DB — janela register->/start: GESTOR sintetizado'
                : '[RBAC] Zero roles do DB — fora da janela: fail-closed (zero sintese)',
              {
                user_type: userType,
                onboarding_status: onbStatus,
                rolesInjected: normalized.map(r => r.role_key),
              },
            );
          }
        }

        const normalizedData = { ...data.data, roles: normalized };
        setRbacData(normalizedData);

        // Seta role primária ou primeira role disponível
        const primary = normalizeRole(data.data.primary_role);
        if (primary) {
          setActiveRole(primary);
        } else if (normalized.length > 0) {
          setActiveRole(normalized[0].role_key);
        }
      }
    } catch (err) {
      console.error('[RBAC] Erro ao carregar contexto:', err);
      setError(err.message);

      // ── SEC (jul/2026, patologia C): catch = estado DESCONHECIDO — NAO
      // sintetiza privilegio a partir de cache local. O codigo anterior:
      //   (1) lia arenastats_active_role e deixava savedRole VENCER — role
      //       REVOGADA ressuscitava num blip do /me (replay mid-session:
      //       login/logout removem a chave, mas revogacao ao vivo + blip
      //       restaurava o privilegio a partir do proprio localStorage);
      //   (2) derivava role de user?.user_type (tambem localStorage);
      //   (3) injetava ALL_ROLES se o email do localStorage estivesse na
      //       allowlist de admin — email e editavel pelo cliente.
      // Ausencia de resposta NAO e afirmacao de privilegio (a forma mais
      // perigosa do "ausencia nao e estado"). roles=[] e fail-closed:
      // RouteGuard branch 0 nega rotas role-gated; public-auth (feed,
      // chat) segue livre; recuperacao via refreshRBAC/reload quando o
      // /me voltar. Flicker num blip e o comportamento correto — a
      // alternativa era ressuscitar privilegio revogado.
      if (process.env.NODE_ENV === 'development') {
        console.log('[RBAC] /me falhou — fail-closed (zero roles sintetizadas):', {
          user_type: user?.user_type,
          error: err.message,
        });
      }

      setRbacData({
        user: {
          user_type: user?.user_type,
          // C2-adjacente (jul/2026): NAO sintetizar 'pending' a partir de erro.
          // Erro do /me = estado DESCONHECIDO, nao um fato sobre onboarding —
          // null = desconhecido; desconhecido NAO funila (shouldFunnelToWizard
          // testa === 'pending').
          onboarding_status: null,
        },
        roles: [],
        permissions: [],
        linked_athletes: [],
        squads: [],
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    loadRBACContext();
  }, [loadRBACContext]);

  // ---- Helpers ----
  const roles = rbacData?.roles?.map(r => r.role_key) || [];
  const permissions = rbacData?.permissions || [];

  const hasRole = useCallback((role) => roles.includes(role), [roles]);
  const hasAnyRole = useCallback((roleList) => roleList.some(r => roles.includes(r)), [roles]);
  const hasPermission = useCallback((perm) => permissions.includes(perm), [permissions]);
  const hasAnyPermission = useCallback((permList) => permList.some(p => permissions.includes(p)), [permissions]);

  const switchRole = useCallback((rawRole) => {
    const role = normalizeRole(rawRole) || rawRole;

    // Super admins can switch to any role (preview mode)
    // Inline check — cannot import ProfileContext (circular dep)
    const adminEmails = (process.env.REACT_APP_GOVERNANCE_ADMIN_EMAILS || '')
      .split(',').map(e => e.trim().toLowerCase()).filter(Boolean);
    const isAdmin = user?.role === 'super_admin' ||
      (user?.email && adminEmails.includes(user.email.toLowerCase()));

    // Normal users can only switch to roles they actually have
    if (!isAdmin && roles.length > 0 && !roles.includes(role)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[RBAC] switchRole denied: "${role}" not in userRoles ${JSON.stringify(roles)}`);
      }
      return; // silently ignore — cannot elevate
    }
    setActiveRole(role);
    localStorage.setItem('arenastats_active_role', role);
  }, [roles, user]);

  // Recuperar role ativa salva (normalizada)
  useEffect(() => {
    const saved = localStorage.getItem('arenastats_active_role');
    if (saved) {
      const normalized = normalizeRole(saved) || saved;
      setActiveRole(normalized);
    }
  }, []);

  const value = {
    // Estado
    rbacData,
    activeRole,
    currentRole: activeRole,     // ← alias (MindPage, FloatingChatWidget usam este nome)
    roles,
    permissions,
    loading,
    error,
    userId: user?.id || null,    // ← exposição direta para pages que precisam
    linkedAthletes: rbacData?.linked_athletes || [],
    squads: rbacData?.squads || [],
    categoryScopes: rbacData?.category_scopes || [],
    clubId: rbacData?.roles?.[0]?.club_id || null,
    isInternal: rbacData?.user?.is_internal === 1 || rbacData?.user?.is_internal === true || false,

    // Helpers
    hasRole,
    hasAnyRole,
    hasPermission,
    hasAnyPermission,
    switchRole,
    refreshRBAC: loadRBACContext,

    // Constants
    ROLES,
    ROLE_LABELS,
    ROLE_ICONS,
  };

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
};
